import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { ProduccionDiariaRow } from '@/types'
import { adherenceBgCls, adherenceTextCls } from '@/utils/adherencia'

interface Props {
  dark: boolean
  rows: ProduccionDiariaRow[]
}

/** Número completo con separador de millar (10.500, no «10,5k»). */
function fmtUds(n: number) {
  if (!n) return '—'
  return n.toLocaleString('es-ES', { maximumFractionDigits: 0 })
}

function fmtDate(iso: string) {
  const [, m, d] = iso.split('-')
  return `${d}/${m}`
}

// Bandas de adherencia centradas en el 100 % (ver utils/adherencia): un 0 % no tiene
// banda propia, se pinta como celda sin dato igual que el null.
function rendBg(pct: number | null, dark: boolean) {
  if (pct === null || pct === 0) return dark ? 'bg-slate-800/40' : 'bg-slate-50'
  return adherenceBgCls(pct, dark)
}
function rendText(pct: number | null, dark: boolean) {
  if (pct === null || pct === 0) return ''
  return adherenceTextCls(pct, dark)
}

export function ProduccionDiariaPivot({ dark, rows }: Props) {
  const { t } = useTranslation()

  const cardBg  = dark ? 'bg-slate-900 border-white/8' : 'bg-white border-slate-200'
  const mutedTxt = dark ? 'text-slate-500' : 'text-slate-400'
  const mainTxt  = dark ? 'text-slate-100' : 'text-slate-800'
  const thCls    = `text-[9px] font-bold uppercase tracking-wider px-2 py-2 whitespace-nowrap ${mutedTxt}`
  const tdCls    = `text-[11px] tabular-nums px-2 py-1.5 text-right`

  const pivot = useMemo(() => {
    const dates = [...new Set(rows.map(r => r.fecha).filter(Boolean) as string[])].sort()
    const familias = [...new Set(rows.map(r => r.familia ?? t('production.kpi.noFamily')))].sort()

    const cells = new Map<string, Map<string, { prod: number; plan: number }>>()
    for (const fam of familias) cells.set(fam, new Map())

    for (const r of rows) {
      if (!r.fecha) continue
      const fam = r.familia ?? t('production.kpi.noFamily')
      const famMap = cells.get(fam)!
      const cur = famMap.get(r.fecha) ?? { prod: 0, plan: 0 }
      famMap.set(r.fecha, { prod: cur.prod + r.qtyProducida, plan: cur.plan + r.qtyPlanificada })
    }

    const colTotals = new Map<string, { prod: number; plan: number }>()
    for (const date of dates) {
      let prod = 0, plan = 0
      for (const fam of familias) {
        const c = cells.get(fam)?.get(date)
        prod += c?.prod ?? 0
        plan += c?.plan ?? 0
      }
      colTotals.set(date, { prod, plan })
    }

    const rowTotals = new Map<string, { prod: number; plan: number }>()
    for (const fam of familias) {
      let prod = 0, plan = 0
      for (const date of dates) {
        const c = cells.get(fam)?.get(date)
        prod += c?.prod ?? 0
        plan += c?.plan ?? 0
      }
      rowTotals.set(fam, { prod, plan })
    }

    return { dates, familias, cells, colTotals, rowTotals }
  }, [rows, t])

  if (!pivot.dates.length || !pivot.familias.length) {
    return (
      <div className={`rounded-xl border p-12 text-center ${cardBg}`}>
        <div className={`text-[12px] ${mutedTxt}`}>{t('production.pivot.noData')}</div>
      </div>
    )
  }

  return (
    <div className={`rounded-xl border overflow-hidden shadow-sm ${cardBg}`}>
      <div className={`px-4 py-2.5 border-b ${dark ? 'border-white/6' : 'border-slate-100'} flex items-center justify-between`}>
        <span className={`text-[11px] font-semibold ${mutedTxt}`}>
          {t('production.pivot.header', { families: pivot.familias.length, days: pivot.dates.length })}
        </span>
        <div className={`flex gap-3 text-[10px] ${mutedTxt}`}>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500/30 inline-block"/>98-102%</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500/30 inline-block"/>97-98% · 102-105%</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-500/30 inline-block"/>&lt;97% · &gt;105%</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead className={dark ? 'bg-slate-800/50' : 'bg-slate-50/80'}>
            <tr>
              <th className={`${thCls} text-left sticky left-0 z-10 min-w-[120px] ${dark ? 'bg-slate-800' : 'bg-slate-50'}`}>
                {t('production.pivot.family')}
              </th>
              {pivot.dates.map(d => (
                <th key={d} className={`${thCls} text-right min-w-[64px]`}>{fmtDate(d)}</th>
              ))}
              <th className={`${thCls} text-right min-w-[80px] ${dark ? 'border-l border-white/8' : 'border-l border-slate-200'}`}>
                {t('production.pivot.total')}
              </th>
              <th className={`${thCls} text-right min-w-[58px]`}>{t('production.pivot.performance')}</th>
            </tr>
          </thead>
          <tbody>
            {pivot.familias.map((fam) => {
              const rowTotal = pivot.rowTotals.get(fam) ?? { prod: 0, plan: 0 }
              const rowPct   = rowTotal.plan > 0 ? Math.round((rowTotal.prod / rowTotal.plan) * 100) : null
              return (
                <tr key={fam}
                  className={`${dark ? 'border-t border-white/4 hover:bg-white/3' : 'border-t border-slate-100 hover:bg-slate-50/40'} transition-colors`}
                >
                  <td className={`px-3 py-2 text-[11px] font-semibold sticky left-0 z-10 ${mainTxt} ${
                    dark ? 'bg-slate-900 border-r border-white/6' : 'bg-white border-r border-slate-100'
                  }`}>
                    {fam}
                  </td>
                  {pivot.dates.map(d => {
                    const cell = pivot.cells.get(fam)?.get(d)
                    const prod = cell?.prod ?? 0
                    const plan = cell?.plan ?? 0
                    const pct  = prod > 0
                      ? (plan > 0 ? Math.round((prod / plan) * 100) : null)
                      : null
                    return (
                      <td key={d}
                        className={`${tdCls} ${prod ? rendBg(pct, dark) : ''} ${prod ? rendText(pct, dark) : mutedTxt}`}
                      >
                        {fmtUds(prod)}
                      </td>
                    )
                  })}
                  <td className={`${tdCls} font-bold ${mainTxt} ${dark ? 'border-l border-white/8' : 'border-l border-slate-200'}`}>
                    {fmtUds(rowTotal.prod)}
                  </td>
                  <td className={`${tdCls} font-semibold ${rowPct !== null ? rendText(rowPct, dark) : mutedTxt}`}>
                    {rowPct !== null ? `${rowPct}%` : '—'}
                  </td>
                </tr>
              )
            })}

            {/* Fila totales */}
            <tr className={`font-bold ${dark ? 'border-t border-white/10 bg-slate-800/60' : 'border-t border-slate-200 bg-slate-50'}`}>
              <td className={`px-3 py-2 text-[11px] font-bold sticky left-0 z-10 ${mainTxt} ${
                dark ? 'bg-slate-800 border-r border-white/8' : 'bg-slate-100 border-r border-slate-200'
              }`}>
                {t('production.pivot.grandTotal')}
              </td>
              {pivot.dates.map(d => {
                const col   = pivot.colTotals.get(d) ?? { prod: 0, plan: 0 }
                const pct   = col.plan > 0 ? Math.round((col.prod / col.plan) * 100) : null
                return (
                  <td key={d} className={`${tdCls} font-bold ${col.prod ? rendText(pct, dark) : mutedTxt} ${col.prod ? rendBg(pct, dark) : ''}`}>
                    {fmtUds(col.prod)}
                  </td>
                )
              })}
              {(() => {
                const grand = [...pivot.rowTotals.values()].reduce((acc, v) => ({ prod: acc.prod + v.prod, plan: acc.plan + v.plan }), { prod: 0, plan: 0 })
                const pct   = grand.plan > 0 ? Math.round((grand.prod / grand.plan) * 100) : null
                return (
                  <>
                    <td className={`${tdCls} font-bold text-[13px] ${dark ? 'border-l border-white/8' : 'border-l border-slate-200'} ${pct !== null ? rendText(pct, dark) : mainTxt}`}>
                      {fmtUds(grand.prod)}
                    </td>
                    <td className={`${tdCls} font-bold text-[13px] ${pct !== null ? rendText(pct, dark) : mainTxt}`}>
                      {pct !== null ? `${pct}%` : '—'}
                    </td>
                  </>
                )
              })()}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
